import React, { useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { api, authState } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { describeApiError } from "../utils/apiErrors";
import { CLERK_ENABLED } from "../lib/clerk";
import { safeReturnUrl } from "../lib/returnUrl";

const SignUpPrompt = ({ t }) => (
  <p className="text-base leading-7 text-muted-foreground text-center">
    {t("auth.noAccountYet")}{" "}
    <Link
      to="/register"
      className="font-semibold text-[#00E88D] underline underline-offset-2 hover:text-[#00c878]"
    >
      {t("auth.signUpHere")}
    </Link>
  </p>
);

const Login = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Clerk flag-gated: niente pagina di login dedicata — si torna alla
  // landing col prompt e lì si apre la modale Clerk in overlay (stessa
  // esperienza del "Crea il mio assistente", stile Lovable).
  //
  // Bypass ?legacy=1: utenti creati PRIMA di Clerk (username/password nel
  // nostro DB, mai esistiti su Clerk) restano bloccati fuori se la modale è
  // l'unica via — il backend supporta ancora il binario JWT legacy
  // (middleware/auth.js), qui gli si dà solo un modo per raggiungerlo.
  if (CLERK_ENABLED && searchParams.get("legacy") !== "1") {
    // La destinazione richiesta (?returnUrl= di ConnectAuthorize, oppure lo
    // state.from di ProtectedRoute) viaggia nello state fino alla landing:
    // è StartPage, a sessione Clerk nata, a riportarci l'utente. Senza,
    // chi arriva dal plugin WordPress (/connect/authorize?…) o da un deep
    // link atterrava sulla home e perdeva la richiesta.
    const from = location.state?.from;
    const returnUrl = safeReturnUrl(
      searchParams.get("returnUrl") ||
        (from ? `${from.pathname || ""}${from.search || ""}` : null)
    );
    return (
      <Navigate to="/" state={{ clerkModal: "signIn", returnUrl }} replace />
    );
  }
  return <NativeLogin />;
};

const NativeLogin = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { refresh } = useAuth();

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      await api.auth.login(data);

      // Refresh auth state to get user data
      const refreshSuccess = await refresh();

      if (!refreshSuccess) {
        throw new Error(
          "Failed to authenticate. Please check your credentials and try again."
        );
      }

      // Redirect: URL di ritorno, oppure il wizard se c'è un draft della
      // landing prompt-first, oppure la home della dashboard (per gli
      // amministratori di sistema la pagina Clienti).
      const from =
        location.state?.from?.pathname ||
        searchParams.get("returnUrl") ||
        (sessionStorage.getItem("pingup-onboarding-mode")
          ? "/bots/new"
          : authState.user?.type === "admin"
            ? "/admin"
            : "/home");
      navigate(from, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(describeApiError(err, t) || t("errors.api.loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-12 md:pt-[120px]">
      <div className="w-full max-w-[601px] flex flex-col gap-10">
        <div className="flex flex-col items-center gap-4 text-center">
          <img
            src="/pingup-logo-horizontal.svg"
            alt="PingUp"
            className="h-[52px] w-auto"
          />
          <h1 className="text-[32px] leading-9 font-semibold text-[#0a0a0a] mt-6">
            {t("auth.welcomeBackTitle")}
          </h1>
          <p className="text-base leading-7 text-muted-foreground">
            {t("auth.welcomeBackSubtitle")}
          </p>
          <SignUpPrompt t={t} />
        </div>

        <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
          <CardHeader>
            <CardTitle>{t("auth.login")}</CardTitle>
            <CardDescription>{t("auth.enterEmailAndPassword")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: t("auth.emailRequired"),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("auth.emailInvalid"),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.email")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("auth.enterEmail")}
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  rules={{
                    required: t("auth.passwordRequired"),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.password")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={t("auth.enterPassword")}
                            autoComplete="current-password"
                            className="pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={t("auth.password")}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? t("auth.signingIn") : t("auth.signIn")}
                </Button>
              </form>
            </Form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">
                    {t("auth.orContinueWith")}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    const returnUrl =
                      searchParams.get("returnUrl") || window.location.href;
                    window.location.href = `/api/sso/login?returnUrl=${encodeURIComponent(
                      returnUrl
                    )}`;
                  }}
                >
                  {t("auth.signInWithSSO")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <SignUpPrompt t={t} />
      </div>
    </div>
  );
};

export default Login;
